import {
  TranscriptData,
  TranscriptParagraph,
} from "@/shared/interfaces/Transcript";
import { paragraphText } from "./timeFormat";

export type WordToken = { text: string; type: "same" | "added" | "removed" };

export interface EntryDiff {
  kind: "added" | "removed" | "modified";
  uid: string;
  before?: TranscriptParagraph;
  after?: TranscriptParagraph;
  /** Speaker label change (modified entries only). */
  speakerChange?: { from: string; to: string };
  /** Time-range change in seconds (modified entries only). */
  rangeChange?: { from: [number, number]; to: [number, number] };
  /** Word-level text diff; present when the text changed (or on add/remove). */
  textTokens?: WordToken[];
}

export interface TranscriptDiff {
  /** Only the entries that changed, in new-version order, removals last. */
  entries: EntryDiff[];
  counts: { added: number; removed: number; modified: number };
}

/**
 * Word-level diff via a longest-common-subsequence walk. Returns tokens tagged
 * `same` / `added` / `removed` so the UI can highlight insertions and deletions.
 */
export const diffWords = (beforeText: string, afterText: string): WordToken[] => {
  const a = beforeText.split(/\s+/).filter(Boolean);
  const b = afterText.split(/\s+/).filter(Boolean);
  const n = a.length;
  const m = b.length;

  // dp[i][j] = LCS length of a[i:] and b[j:].
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0),
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        a[i] === b[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const tokens: WordToken[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      tokens.push({ text: a[i], type: "same" });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      tokens.push({ text: a[i], type: "removed" });
      i++;
    } else {
      tokens.push({ text: b[j], type: "added" });
      j++;
    }
  }
  while (i < n) tokens.push({ text: a[i++], type: "removed" });
  while (j < m) tokens.push({ text: b[j++], type: "added" });
  return tokens;
};

/**
 * Compares the loaded transcript to the working draft, matching entries by
 * their transient `_uid`. Entries present only in the draft are `added`, only
 * in the original are `removed`, and those in both with any speaker/time/text
 * change are `modified`. Unchanged entries are omitted.
 */
export const diffTranscripts = (
  original: TranscriptData | null,
  draft: TranscriptData | null,
): TranscriptDiff => {
  const origByUid = new Map<string, TranscriptParagraph>();
  for (const p of original?.paragraphs ?? []) {
    if (p._uid) origByUid.set(p._uid, p);
  }

  const draftUids = new Set<string>();
  const entries: EntryDiff[] = [];
  let added = 0;
  let removed = 0;
  let modified = 0;

  for (const p of draft?.paragraphs ?? []) {
    const uid = p._uid as string;
    draftUids.add(uid);
    const before = origByUid.get(uid);

    if (!before) {
      added++;
      entries.push({
        kind: "added",
        uid,
        after: p,
        textTokens: diffWords("", paragraphText(p)),
      });
      continue;
    }

    const speakerFrom = String(before.speaker ?? "");
    const speakerTo = String(p.speaker ?? "");
    const beforeText = paragraphText(before);
    const afterText = paragraphText(p);
    const speakerChanged = speakerFrom !== speakerTo;
    const rangeChanged = before.start !== p.start || before.end !== p.end;
    const textChanged = beforeText !== afterText;

    if (!speakerChanged && !rangeChanged && !textChanged) continue;

    modified++;
    entries.push({
      kind: "modified",
      uid,
      before,
      after: p,
      speakerChange: speakerChanged
        ? { from: speakerFrom, to: speakerTo }
        : undefined,
      rangeChange: rangeChanged
        ? { from: [before.start, before.end], to: [p.start, p.end] }
        : undefined,
      textTokens: textChanged ? diffWords(beforeText, afterText) : undefined,
    });
  }

  for (const p of original?.paragraphs ?? []) {
    const uid = p._uid as string;
    if (draftUids.has(uid)) continue;
    removed++;
    entries.push({
      kind: "removed",
      uid,
      before: p,
      textTokens: diffWords(paragraphText(p), ""),
    });
  }

  return { entries, counts: { added, removed, modified } };
};
